import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/useToast'
import { Loader2, Shield } from 'lucide-react'
import type { Factor } from '@supabase/supabase-js'

export function MfaVerifyForm() {
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [selectedFactor, setSelectedFactor] = useState<Factor | null>(null)
  const { verifyMfa, mfaFactors, adminUser } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()

  useEffect(() => {
    const verifiedFactors = mfaFactors.filter((f: Factor) => f.status === 'verified')
    if (verifiedFactors.length > 0) {
      setSelectedFactor(verifiedFactors[0])
    }
  }, [mfaFactors])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedFactor) {
      toast({
        title: 'Error',
        description: 'No MFA factor found',
        variant: 'destructive',
      })
      return
    }

    setIsLoading(true)

    const { error } = await verifyMfa(code, selectedFactor.id)

    if (error) {
      toast({
        title: 'Verification Failed',
        description: error.message,
        variant: 'destructive',
      })
      setIsLoading(false)
      return
    }

    navigate(adminUser?.role === 'investor' ? '/investor' : '/dashboard')
    setIsLoading(false)
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-center">
        <div className="p-3 rounded-xl bg-brand-fg/10">
          <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-brand-fg" />
        </div>
      </div>

      <div className="text-center">
        <h2 className="text-lg sm:text-xl font-semibold">Two-Factor Authentication</h2>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">
          Enter the code from your authenticator app
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex justify-center gap-1.5 sm:gap-2">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="w-9 h-11 sm:w-10 sm:h-12 border rounded-lg flex items-center justify-center text-base sm:text-lg font-mono bg-muted/50"
            >
              {code[i] || ''}
            </div>
          ))}
        </div>

        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={code}
          onChange={handleCodeChange}
          className="sr-only"
          autoFocus
          autoComplete="one-time-code"
        />

        <p className="text-xs text-center text-muted-foreground px-4">
          Click the boxes above and type your 6-digit code
        </p>

        <Button
          type="submit"
          className="w-full bg-dextoro text-black hover:bg-dextoro/90"
          disabled={isLoading || code.length !== 6}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify'
          )}
        </Button>
      </form>
    </div>
  )
}
